/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box, Typography, Grid, Button, TextField,
  useTheme,
  styled,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  useMediaQuery,
  Tooltip,
} from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CircularProgress from '@material-ui/core/CircularProgress';
import { cartAddAction, applyPromotionalCodeAction } from '../../../actions/license.action';

const useStyles = makeStyles((theme) => ({
  summaryBox: {
    padding: theme.spacing(3),
    backgroundColor: theme.body.form.inputBackgroundColor,
    borderRadius: '16px',
    border: `1px solid ${theme.body.cart.products.secondary}`,
  },
  stickyContainer: {
    position: 'sticky',
    top: '100px',
  },
  promoInput: {
    width: '203px',
    height: '48px',
    padding: '12px 16px',
    borderRadius: '10px',
    border: `1px solid ${theme.body.form2.border}`,
    outline: 'none',
    fontSize: '1rem',
    fontFamily: 'Inter, sans-serif',
    boxSizing: 'border-box',
  },
  applyButton: {
    width: '133px',
    height: '48px',
    minWidth: '112px',
    borderRadius: '10px',
    backgroundColor: theme.body.form.inputBackgroundColor,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    textTransform: 'none',
    fontWeight: 'bold',
    padding: '14px 16px',
    transition: '0.3s',
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
      borderColor: theme.palette.primary.main,
    },
    fontFamily: '"Karla"',
    fontSize: '16px',
    letterSpacing: 0,
  },
  promoContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '24px',
    marginBottom: '24px',
    gap: '12px',
  },
  textFieldRoot: {
    width: '203px',
    height: '48px',
    '& .MuiOutlinedInput-root': {
      height: '48px',
      borderRadius: '10px',
      backgroundColor: theme.body.form.inputBackgroundColor,
      '& fieldset': {
        borderColor: theme.body.form2.border,
      },
      '&:hover fieldset': {
        borderColor: theme.body.form.titleColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '12px 16px',
      fontFamily: '"Raleway"',
      fontSize: '14px',
      color: theme.body.form.titleColor,
    },
  },
  continueButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.body.form.inputBackgroundColor,
    padding: '10px',
    fontWeight: 'bold',
    borderRadius: '8px',
    textTransform: 'none',
    fontSize: '1rem',
    marginBottom: '8px',
    '&:hover': {
      backgroundColor: theme.palette.primary.hover,
    },
    fontFamily: '"Karla"',
  },
  viewMoreButton: {
    backgroundColor: theme.body.form.inputBackgroundColor,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    padding: '10px',
    fontWeight: 'bold',
    borderRadius: '8px',
    textTransform: 'none',
    fontSize: '1rem',
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
    fontFamily: '"Karla"',
  },
}));

const OwnTypography = styled(Typography)(({ theme }) => ({
  color: theme.body.form.titleColor,
  fontSize: '24px',
  lineHeight: '2rem',
  letterSpacing: '0%',
  verticalAlign: 'middle',
  display: 'inline-flex',
  alignItems: 'center',
  fontFamily: '"Karla"',
}));

const OwnTypographyTable = styled(Typography)(({ theme }) => ({
  color: theme.body.form.titleColor,
  fontSize: '16px',
  fontWeight: 400,
  fontFamily: '"Karla"',
  letterSpacing: 0,
}));

const OwnTableCell = styled(TableCell)(() => ({
  borderBottom: 'none',
  paddingTop: '2px',
}));

const PurchaseSummary = (props) => {
  const {
    totalProductos = 0, totalNeto = 0, handleNext, currencySymbol = '$', activeStep,
    totalIva = 0, totalRetenciones = 0, granTotal = 0, errors, licenseLoadingSaveOrder,
    currency = 'COP', items, cartData, code, cartAdd, loadingViewMore, setLoadingViewMore,
    saveOrderMarketError, applyPromotionalCode, appliedStrings, valorDescuento,
    loadingValidatePromoCode, promoFeedback, rulesPromoCode, percentageIva, tieneRetenciones,
  } = props;
  const classes = useStyles();
  const [promoCode, setPromoCode] = useState('');
  const errorList = Object.keys(errors ?? {});
  const theme = useTheme();
  const isUpLg = useMediaQuery(theme.breakpoints.up('lg'));
  const cleanRegex = rulesPromoCode?.cleanRegex ?? /[^a-zA-Z0-9]/g;
  const maxLengthProCode = rulesPromoCode?.rules?.maxLength?.value ?? 20;
  const esExitoPromo = promoFeedback.message && !promoFeedback.isError;

  let displayMessage = '';

  if (saveOrderMarketError) {
    displayMessage = saveOrderMarketError;
  } else if (promoFeedback.message) {
    displayMessage = promoFeedback.message;
  } else if (errorList.length > 0) {
    displayMessage = errorList[0] === 'terminos'
      ? 'Debes aceptar los Términos y condiciones y Autorización para el Tratamiento de Datos Personales.'
      : 'Debes completar los campos obligatorios.';
  }

  const esErrorDeTerminos = errorList.length > 0 && errorList[0] === 'terminos';

  const handleApplyPromo = () => {
    if (!promoCode.trim()) {
      return;
    }
    applyPromotionalCode(promoCode, appliedStrings);
    setPromoCode('');
  };

  const formatMoney = (value) => (value ?? 0).toLocaleString('es-CO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const formatMoneyTotal = (value) => {
    const num = value ?? 0;
    const decimal = Number((num % 1).toFixed(2));
    const finalValue = decimal >= 0.95 ? Math.ceil(num) : num;
    return finalValue.toLocaleString('es-CO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const viewMoreProducts = async () => {
    if (loadingViewMore) return;
    setLoadingViewMore(true);
    const formattedCartItems = items.map((item) => ({
      packageCode: item.packageCode,
      quantity: item.quantity,
      validityPeriod: item.validityPeriod,
      validityType: item.validityTypeId,
      deviceCode: item.deviceTypeCode,
    }));

    const payload = {
      countryCode: cartData?.countryCode,
      regionCode: cartData?.regionCode,
      transactionCode: code,
      cartItems: formattedCartItems,
    };

    try {
      await cartAdd(payload);
      window.location.href = `${cartData?.urlMarketPlace}?code=${code}`;
    } catch (error) {
      setLoadingViewMore(false);
      window.location.href = `${cartData?.urlMarketPlace}?code=${code}`;
    }
  };

  return (
    <Grid item xs={12} md={4}>
      <Box className={classes.stickyContainer}>
        <Box className={classes.summaryBox}>
          <Typography
            style={{
              fontWeight: 700,
              color: theme.body.form.titleColor,
              fontSize: '1.25rem',
              marginBottom: '24px',
              letterSpacing: 0,
              fontFamily: '"Karla"',
            }}
          >
            {activeStep === 2 ? `Productos: ${totalProductos}` : 'Resumen de la compra'}
          </Typography>
          {activeStep !== 2 && (
            <Typography
              style={{
                color: theme.body.form.titleColor,
                fontSize: '1rem',
                fontWeight: 400,
                marginBottom: '24px',
                letterSpacing: 0,
                fontFamily: '"Raleway"',
              }}
            >
              Total productos:
              {' '}
              <span style={{ letterSpacing: 0, fontFamily: '"Karla"' }}>
                {totalProductos}
              </span>
            </Typography>
          )}

          <TableContainer
            style={{
              overflow: 'hidden',
              boxShadow: 'none',
              marginBottom: saveOrderMarketError ? '24px' : 0,
            }}
          >
            <Table
              size="small"
            >
              <TableBody>
                <TableRow
                  id={totalNeto}
                  style={{
                    paddingBottom: 0,
                  }}
                >
                  <OwnTableCell
                    style={{
                      textAlign: 'left',
                      paddingLeft: 0,
                    }}
                  >
                    <OwnTypographyTable>
                      Total bruto:
                    </OwnTypographyTable>
                  </OwnTableCell>
                  {isUpLg && (
                    <>
                      <OwnTableCell />
                      <OwnTableCell />
                    </>
                  )}
                  <OwnTableCell
                    style={{
                      textAlign: 'right',
                      paddingRight: 0,
                    }}
                  >
                    <OwnTypographyTable>
                      {currencySymbol}
                    </OwnTypographyTable>
                  </OwnTableCell>
                  <OwnTableCell
                    style={{
                      textAlign: 'right',
                    }}
                  >
                    <Tooltip title={formatMoney(totalNeto)} placement="top">
                      <OwnTypographyTable>
                        {formatMoney(totalNeto)}
                      </OwnTypographyTable>
                    </Tooltip>
                  </OwnTableCell>
                </TableRow>
                {valorDescuento > 0 && (
                  <TableRow id={valorDescuento}>
                    <OwnTableCell style={{ textAlign: 'left', paddingLeft: 0 }}>
                      <OwnTypographyTable>
                        Descuento:
                      </OwnTypographyTable>
                    </OwnTableCell>
                    {isUpLg && (
                      <>
                        <OwnTableCell />
                        <OwnTableCell />
                      </>
                    )}
                    <OwnTableCell style={{ textAlign: 'right', paddingRight: 0 }}>
                      <OwnTypographyTable>
                        {currencySymbol}
                      </OwnTypographyTable>
                    </OwnTableCell>
                    <OwnTableCell style={{ textAlign: 'right' }}>
                      <Tooltip title={formatMoney(valorDescuento)} placement="top">
                        <OwnTypographyTable>
                          {formatMoney(valorDescuento)}
                        </OwnTypographyTable>
                      </Tooltip>
                    </OwnTableCell>
                  </TableRow>
                )}

                <TableRow id={totalIva}>
                  <OwnTableCell
                    style={{
                      textAlign: 'left',
                      paddingLeft: 0,
                    }}
                  >
                    <OwnTypographyTable>
                      IVA
                      {' '}
                      {percentageIva}
                      :
                    </OwnTypographyTable>
                  </OwnTableCell>
                  {isUpLg && (
                    <>
                      <OwnTableCell />
                      <OwnTableCell />
                    </>
                  )}
                  <OwnTableCell
                    style={{
                      textAlign: 'right',
                      paddingRight: 0,
                    }}
                  >
                    <OwnTypographyTable>
                      {currencySymbol}
                    </OwnTypographyTable>
                  </OwnTableCell>
                  <OwnTableCell
                    style={{
                      textAlign: 'right',
                    }}
                  >
                    <Tooltip title={formatMoney(totalIva)} placement="top">
                      <OwnTypographyTable>
                        {formatMoney(totalIva)}
                      </OwnTypographyTable>
                    </Tooltip>
                  </OwnTableCell>
                </TableRow>

                {tieneRetenciones && (
                  <TableRow id={totalRetenciones}>
                    <OwnTableCell
                      style={{
                        textAlign: 'left',
                        paddingLeft: 0,
                      }}
                    >
                      <OwnTypographyTable>
                        Retenciones:
                      </OwnTypographyTable>
                    </OwnTableCell>
                    {isUpLg && (
                      <>
                        <OwnTableCell />
                        <OwnTableCell />
                      </>
                    )}
                    <OwnTableCell
                      style={{
                        textAlign: 'right',
                        paddingRight: 0,
                      }}
                    >
                      <OwnTypographyTable>
                        {currencySymbol}
                      </OwnTypographyTable>
                    </OwnTableCell>
                    <OwnTableCell
                      style={{
                        textAlign: 'right',
                      }}
                    >
                      <Tooltip title={formatMoney(totalRetenciones)} placement="top">
                        <OwnTypographyTable>
                          {formatMoney(totalRetenciones)}
                        </OwnTypographyTable>
                      </Tooltip>
                    </OwnTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {activeStep !== 2 && (
            <Box className={classes.promoContainer}>
              <TextField
                placeholder="Código promocional"
                variant="outlined"
                className={classes.textFieldRoot}
                value={promoCode}
                onChange={(e) => {
                  const { value } = e.target;
                  const cleanValue = value.replace(cleanRegex, '');
                  setPromoCode(cleanValue);
                }}
                inputProps={{
                  autoComplete: 'off',
                  maxLength: maxLengthProCode,
                }}
                disabled={loadingViewMore}
              />
              <Button className={classes.applyButton} variant="contained" disabled={loadingValidatePromoCode || loadingViewMore} disableElevation onClick={handleApplyPromo}>
                {loadingValidatePromoCode ? (
                  <CircularProgress size={24} style={{ color: 'white' }} />
                ) : (
                  'Aplicar'
                )}
              </Button>
            </Box>
          )}
          {(saveOrderMarketError || errorList.length > 0 || promoFeedback.message) && (
            <>
              <Box style={{
                backgroundColor: esExitoPromo
                  ? theme.body.promotionalCodes.success.backgroundColor
                  : theme.body.form.bkgColorErr,
                display: 'flex',
                alignItems: esErrorDeTerminos ? 'flex-start' : 'center',
                justifyContent: 'flex-start',
                paddingLeft: 16,
                paddingRight: 16,
                paddingTop: 13,
                paddingBottom: 13,
                borderRadius: 12,
              }}
              >
                {esExitoPromo ? (
                  <CheckCircleIcon style={{
                    color: theme.body.promotionalCodes.success.colorIcon,
                    marginRight: 8,
                    flexShrink: 0,
                  }}
                  />
                ) : (
                  <ErrorIcon style={{
                    color: theme.body.form.iconErrColor,
                    marginRight: 8,
                    flexShrink: 0,
                  }}
                  />
                )}

                <Typography style={{
                  color: esExitoPromo
                    ? theme.body.promotionalCodes.success.colorText
                    : theme.body.form.colorErrText,
                  fontSize: 14,
                  lineHeight: !esErrorDeTerminos && '1.8',
                  letterSpacing: 0,
                  fontFamily: '"Raleway"',
                }}
                >
                  {displayMessage}
                </Typography>
              </Box>
              <Box sx={{ mt: 3 }} />
            </>
          )}
          <Box
            display={activeStep === 2 ? 'flex' : ''}
            flexDirection="row"
            alignItems="center"
            justifyContent={activeStep === 2 ? 'flex-end' : 'flex-start'}
            mt={activeStep === 2 ? 4 : 0}
          >
            <Typography
              style={{
                color: theme.body.form.titleColor,
                fontWeight: 500,
                fontSize: '1rem',
                lineHeight: '18px',
                marginRight: '8px',
                verticalAlign: 'middle',
                letterSpacing: 0,
                fontFamily: '"Raleway"',
              }}
            >
              Valor total:
            </Typography>
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <OwnTypography
                style={{
                  fontWeight: 700,
                }}
              >
                {currencySymbol}
                {formatMoneyTotal(granTotal)}
              </OwnTypography>
              <OwnTypography
                style={{
                  fontWeight: 400,
                }}
              >
                {currency}
              </OwnTypography>
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" mt={3}>
            <Button
              fullWidth
              variant="contained"
              className={classes.continueButton}
              onClick={handleNext}
              disableElevation
              disabled={licenseLoadingSaveOrder || loadingViewMore}
            >
              {licenseLoadingSaveOrder ? (
                <CircularProgress size={24} style={{ color: 'white' }} />
              ) : (
                activeStep === 2 ? 'Ir a pagar' : 'Continuar'
              )}
            </Button>

            {activeStep === 0 && (
              <Button
                fullWidth
                variant="outlined"
                className={classes.viewMoreButton}
                onClick={viewMoreProducts}
                disabled={loadingViewMore}
              >
                {loadingViewMore ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Ver más productos'
                )}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Grid>
  );
};

const mapStateToProps = () => ({
});

const mapDispatchToProps = {
  cartAdd: cartAddAction,
  applyPromotionalCode: applyPromotionalCodeAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(PurchaseSummary);
